import { Crown, Check, X, Zap, Star, TrendingUp, Shield, CreditCard, RefreshCw, Mail, Target, BarChart3, Heart, Sparkles, AlertTriangle, Video, Search, MessageCircle, Bookmark, ChevronRight, ChevronLeft, ArrowLeft, Briefcase } from 'lucide-react';
import { Loader } from './ui/Loader';
import { projectId, API_BASE } from '../utils/supabase/info';
import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { toast } from '../utils/toast';
import { UpgradeConfirmationModal } from './UpgradeConfirmationModal';
import { devLog, devError } from '../utils/logger';
import { isSwUpdateGuardActive } from '../utils/swUpdateState';
import { canPurchaseEmployerSubscription, isEmployerApproved } from '../utils/accessPolicy';
import { PageShell } from './layout/PageShell';

interface SubscriptionPageProps {
  accessToken: string;
  userId: string;
  onBack: () => void;
  onUpgrade: () => void;
  onCheckout?: (planType: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus', priceId: string) => void;
  currentUserProfile?: any; // ✅ NEW: Accept current profile from App.tsx
  isVisible?: boolean; // ✅ NEW: Track when page is visible
  setSubscriptionType?: (type: 'plus' | 'employer' | 'employer_plus' | null) => void; // ✅ NEW: For ThankYou page
  navigateTo?: (page: string) => void; // ✅ NEW: For navigation
  onOpenCancelModal?: (subscriptionEndDate: string, currentPlan: string, onConfirm: () => void) => void; // ✅ Simplified: Only basic cancel, downgrade moved to separate modal
  onOpenReactivateModal?: (currentPlan: string, onConfirm: () => void) => void; // ✅ NEW: Callback for reactivate modal (moved to App.tsx)
  refetchUserProfile?: () => Promise<void>; // ✅ NEW: Callback to refresh user profile in App.tsx
  registerRefetchSubscription?: (fn: (() => void) | null) => void; // App stores ref so it can trigger refetch when cancellation confirmation is closed
  onOpenUpdatePayment?: (returnTo: 'subscription' | 'settings', planType?: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus') => void; // Opens checkout in update-payment mode; after success user returns to returnTo
}

export function SubscriptionPage({ 
  accessToken, 
  userId, 
  onBack, 
  onUpgrade, 
  onCheckout, 
  currentUserProfile, 
  isVisible = true,
  setSubscriptionType,
  navigateTo,
  onOpenCancelModal,
  onOpenReactivateModal,
  refetchUserProfile,
  registerRefetchSubscription,
  onOpenUpdatePayment,
}: SubscriptionPageProps) {
  const [userProfile, setUserProfile] = useState<any>(currentUserProfile || null); // ✅ Initialize with prop
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  
  // ✅ NEW: Upgrade confirmation modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    planType: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus';
    priceId: string;
    planName: string;
    monthlyPrice: number;
  } | null>(null);
  
  // ✅ NEW: Downgrade confirmation modal state
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  
  // ✅ NEW: Cancel downgrade confirmation modal state
  const [showCancelDowngradeModal, setShowCancelDowngradeModal] = useState(false);
  const [cancelDowngradeLoading, setCancelDowngradeLoading] = useState(false);
  
  // ✅ NEW: Stripe Price IDs from backend (environment variables)
  const [priceIds, setPriceIds] = useState<{
    talentPlussMonthly: string;
    arbeidsgiverMessagingMonthly: string;
    arbeidsgiverPlussMonthly: string;
  } | null>(null);
  
  // iOS PWA: sub-page header + scroll-to-top (same pattern as FavoritesPage, MyGigsPage, etc.)
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const el = subPageContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [isVisible]);

  useEffect(() => {
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Keep local profile state in sync with App profile state.
  useEffect(() => {
    if (currentUserProfile) {
      devLog('🔄 SubscriptionPage: Updating userProfile from prop', {
        verificationStatus: currentUserProfile.verificationStatus,
        subscription: currentUserProfile.subscription
      });
      setUserProfile(currentUserProfile);
    }
  }, [currentUserProfile]);

  // ✅ FETCH PRICE IDS FROM BACKEND
  useEffect(() => {
    const isTransientAuthOrUpdateError = (error: unknown) => {
      const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
      return (
        message.includes('abort') ||
        message.includes('aborted') ||
        message.includes('temporary failure') ||
        message.includes('failed to fetch') ||
        message.includes('network')
      );
    };

    const fetchPriceIds = async () => {
      try {
        if (!accessToken) return;
        const response = await fetch(`${API_BASE}/nets/plans`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          // Nets is amount-based, but we keep this mapping to avoid rewriting the checkout-view wiring.
          setPriceIds({
            talentPlussMonthly: 'talent_plus',
            arbeidsgiverMessagingMonthly: 'arbeidsgiver',
            arbeidsgiverPlussMonthly: 'arbeidsgiver_plus',
          });
          devLog('✅ Nets plans loaded:', data?.plans || data);
        } else {
          devError('❌ Failed to load price IDs');
          if (response.status === 401 || response.status === 403 || isSwUpdateGuardActive()) {
            devLog('ℹ️ Skipping subscription price toast during transient auth/update state');
            return;
          }
          toast.error('Kunne ikke laste abonnementspriser');
        }
      } catch (error) {
        devError('❌ Price ID fetch error:', error);
        if (isSwUpdateGuardActive() || isTransientAuthOrUpdateError(error)) {
          devLog('ℹ️ Skipping subscription price toast for transient update/auth error');
          return;
        }
        toast.error('Kunne ikke laste abonnementspriser');
      }
    };
    
    fetchPriceIds();
  }, [accessToken]);
  
  // ✅ NEW: Upgrade/Downgrade handler
  const handleUpgradeSubscription = async (planType: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus', priceId: string) => {
    try {
      // ✅ FIX: Don't use global loading state - modal button handles upgradeLoading
      
      const response = await fetch(
        `${API_BASE}/nets/upgrade-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          // For Nets we use a robust policy: plan change takes effect at next renewal.
          body: JSON.stringify({ priceId, planType }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Oppgraderingen er planlagt og trer i kraft ved neste fornyelse.');
        // Refresh data
        await fetchProfile();
        await fetchSubscriptionStatus();
        onUpgrade(); // Trigger profile refresh in App.tsx
      } else {
        toast.error('Feil: ' + (data.error || 'Ukjent feil'));
      }
    } catch (error) {
      devError('Upgrade error:', error);
      toast.error('Kunne ikke oppgradere abonnement');
    }
    // ✅ FIX: No finally block with setLoading(false) - modal button handles loading state
  };
  
  // ⚠️ NEW: Check employer verification before allowing checkout/upgrade
  const handleCheckoutClick = (planType: 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus', priceId: string) => {
    devLog('🛒 Checkout click:', { 
      planType, 
      userType: userProfile?.userType, 
      verificationStatus: userProfile?.verificationStatus 
    });
    
    // Only employers need verification check
    if (userProfile?.userType === 'employer' && !isEmployerApproved(userProfile)) {
      const statusMessage = userProfile?.verificationStatus === 'pending'
        ? 'Din bedriftskonto venter på godkjenning fra administrator. Du vil motta en e-post når kontoen er godkjent og kan kjøpe abonnement.'
        : 'Din bedriftskonto har blitt avvist og kan ikke kjøpe abonnement. Ta kontakt med support for mer informasjon.';
      
      devLog('⛔ Employer not verified:', statusMessage);
      toast.warning(statusMessage);
      return;
    }
    
    // Check if user already has subscription (should upgrade instead)
    if (hasAnyPaidSubscription && isActive) {
      // ✅ NEW: Open confirmation modal first, then upgrade
      const planName = planType === 'talent_plus' ? 'Talentfunn Pluss' 
        : planType === 'arbeidsgiver' ? 'Arbeidsgiver Basis'
        : 'Arbeidsgiver Pluss';
      
      const monthlyPrice = planType === 'talent_plus' ? 149
        : planType === 'arbeidsgiver' ? 490
        : 990;
      
      setPendingUpgrade({ planType, priceId, planName, monthlyPrice });
      setShowUpgradeModal(true);
    } else {
      // Proceed with normal checkout
      if (onCheckout) {
        onCheckout(planType, priceId);
      }
    }
  };
  
  // ✅ NEW: Handle confirmed upgrade (after modal confirmation)
  const handleConfirmUpgrade = async () => {
    if (pendingUpgrade) {
      await handleUpgradeSubscription(pendingUpgrade.planType, pendingUpgrade.priceId);
      setPendingUpgrade(null);
    }
  };
  
  // ✅ NEW: Refresh data when page becomes visible
  useEffect(() => {
    if (isVisible) {
      devLog('📊 SubscriptionPage became visible - refreshing data...');
      fetchSubscriptionStatus(); // Refresh subscription status from Stripe
    }
  }, [isVisible]);
  
  // Refetch profile basics when accessToken changes (e.g. after re-login).
  // Subscription status fetching is handled by the dedicated visibility effect.
  useEffect(() => {
    if (!accessToken) return;
    if (!currentUserProfile) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  const fetchProfile = async () => {
    try {
      if (!accessToken) return;
      const response = await fetch(`${API_BASE}/profile/${userId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
      }
    } catch (error) {
      devLog('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      if (!accessToken) return;
      const response = await fetch(`${API_BASE}/nets/subscription-status`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        devError('subscription-status request failed', {
          status: response.status,
          requestId: data?.requestId ?? response.headers.get('X-Request-Id') ?? undefined,
          stage: data?.stage,
          error: data?.error,
          details: data?.details,
        });
        return;
      }

      devLog('🔍 Subscription Status Response:', data?.subscription);
      setSubscriptionData(data?.subscription || null);
    } catch (error) {
      devLog('Error fetching subscription:', error);
    }
  };

  useEffect(() => {
    registerRefetchSubscription?.(fetchSubscriptionStatus);
    return () => {
      registerRefetchSubscription?.(null);
    };
  }, [registerRefetchSubscription, fetchSubscriptionStatus]);

  // Check current plan
  const currentPlan = userProfile?.subscription || 'free';
  const isActive = subscriptionData?.status === 'active';
  const isCanceled = subscriptionData?.cancelAtPeriodEnd === true || subscriptionData?.cancel_at_period_end === true;
  const isEmployer = userProfile?.userType === 'employer';
  
  // ✅ NEW: Check for pending downgrade
  const pendingPlan = subscriptionData?.pendingPlan || null;
  const hasPendingDowngrade = pendingPlan === 'employer'; // ✅ Specifically check if downgrading to employer

  // ✅ FIX: Use profile as source of truth - it's updated immediately after payment
  // Stripe subscription data might not be synced yet, causing UI to show wrong state
  const hasPlusSubscription = currentPlan === 'plus';
  const hasEmployerSubscription = currentPlan === 'employer';
  const hasEmployerPlusSubscription = currentPlan === 'employer_plus';
  const hasAnyPaidSubscription = hasPlusSubscription || hasEmployerSubscription || hasEmployerPlusSubscription;
  
  // ✅ Show active state if user has subscription in profile (source of truth)
  // This ensures UI updates immediately after successful payment
  const showAsActive = hasPlusSubscription || hasEmployerSubscription || hasEmployerPlusSubscription || isActive;

  // Calculate days remaining
  const calculateDaysRemaining = () => {
    if (!subscriptionData?.currentPeriodEnd) {
      // ✅ FIX: Fallback for new subscriptions (webhook hasn't set currentPeriodEnd yet)
      // If user has active subscription but no end date yet, assume 30 days
      if (hasAnyPaidSubscription && isActive) {
        return 30; // Fallback for newly created subscriptions
      }
      return null;
    }
    const endDate = new Date(subscriptionData.currentPeriodEnd);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = calculateDaysRemaining();

  // Format subscription end date
  const formatEndDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // ✅ FIX: Keep ISO string for App.tsx calculations, format for display only
  const subscriptionEndDateISO = subscriptionData?.currentPeriodEnd || '';
  const subscriptionEndDateFormatted = subscriptionData?.currentPeriodEnd 
    ? formatEndDate(subscriptionData.currentPeriodEnd)
    : '';

  const handleCancel = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/nets/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setCancelSuccess(true);
        setCancelError('');
        await fetchSubscriptionStatus();
        if (refetchUserProfile) await refetchUserProfile();
        // Delayed refetch so UI reliably shows canceled state (handles DB/response timing)
        setTimeout(() => {
          fetchSubscriptionStatus();
          if (refetchUserProfile) void refetchUserProfile();
        }, 800);
      } else {
        const data = await response.json().catch(() => null);
        const message = data?.error || 'Noe gikk galt. Prøv igjen senere.';
        setCancelError(message);
        toast.error(message);
      }
    } catch (error) {
      devLog('Error canceling subscription:', error);
      setCancelError('Noe gikk galt. Prøv igjen senere.');
      toast.error('Kunne ikke avslutte abonnement. Prøv igjen senere.');
    }
  };

  // ✅ NEW: Downgrade from employer_plus to employer (490 kr)
  const handleDowngrade = async () => {
    try {
      // ✅ FIX: Don't use global loading state - modal button handles downgradeLoading
      const response = await fetch(
        `${API_BASE}/nets/downgrade-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ 
            planType: 'arbeidsgiver', // Downgrade to employer
            priceId: priceIds?.arbeidsgiverMessagingMonthly || 'arbeidsgiver_messaging_monthly' 
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Nedgradering til Basis planlagt!');
        await fetchSubscriptionStatus();
        // ✅ FIX: Refresh user profile to update UI immediately
        if (refetchUserProfile) {
          await refetchUserProfile();
        }
      } else {
        toast.error('Feil: ' + (data.error || 'Kunne ikke nedgradere'));
      }
    } catch (error) {
      devError('Downgrade error:', error);
      toast.error('Kunne ikke nedgradere abonnement');
    }
    // ✅ FIX: No finally block with setLoading(false) - modal button handles loading state
  };

  const handleReactivate = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/nets/reactivate-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        toast.success('Abonnementet er reaktivert');
        await fetchSubscriptionStatus();
        if (refetchUserProfile) await refetchUserProfile();
      } else {
        setCancelError('Noe gikk galt. Prøv igjen senere.');
      }
    } catch (error) {
      devLog('Error reactivating subscription:', error);
      setCancelError('Noe gikk galt. Prøv igjen senere.');
    }
  };

  const handleOpenUpdatePayment = () => {
    const planType = (subscriptionData?.planType || 'talent_plus') as 'talent_plus' | 'arbeidsgiver' | 'arbeidsgiver_plus';
    if (onOpenUpdatePayment) {
      onOpenUpdatePayment('subscription', planType);
      return;
    }
    if (onCheckout) {
      onCheckout(planType, '__UPDATE_PAYMENT_DETAILS__');
    }
  };

  // Sub-page header (same pattern as FavoritesPage, MyGigsPage); hidden on desktop (global header used there)
  const subPageHeader = (
    <div ref={stickyHeaderRef} className="sub-page-header xl:hidden bg-white border-b border-gray-200 px-4 pt-4 pb-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
      <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-150 active:scale-95" aria-label="Tilbake">
        <ArrowLeft className="w-5 h-5" />
      </button>
      <div className="flex-1 flex items-center gap-3">
        <Crown className="w-8 h-8 text-purple-600 flex-shrink-0" />
        <div>
          <h2 className="font-semibold">Abonnement</h2>
          <p className="text-sm text-gray-600">
            {loading ? 'Laster...' : subscriptionData?.subscription?.plan_name ? subscriptionData.subscription.plan_name : userProfile?.subscription ? 'Din plan' : 'Velg plan'}
          </p>
        </div>
      </div>
    </div>
  );

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-orange-50 xl:overflow-hidden">
        <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />
        {subPageHeader}
        <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
          <PageShell className="px-4 py-4 xl:max-w-6xl xl:mx-auto xl:py-6">
            <div className="text-center py-12">
              <Loader size="2xl" className="text-purple-600" />
              <p className="mt-4 text-purple-900">Laster...</p>
            </div>
          </PageShell>
        </div>
      </div>
    );
  }

  // EMPLOYER VIEW - 3 Plans: Free, Messaging, Plus
  if (isEmployer) {
    return (
      <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-orange-50 xl:overflow-hidden">
        <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />
        {subPageHeader}
        <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
          <PageShell className="px-4 py-4 xl:max-w-6xl xl:mx-auto xl:py-6">
          {/* Content container - rounded on desktop only */}
          <div className="xl:pt-5 xl:bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:p-8">
            {/* Header */}
            <div className="text-center mb-12 px-4 xl:px-0">
              <h1 className="text-5xl bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-2 mb-4">
                Arbeidsgiver-abonnementer
              </h1>
              <p className="text-xl text-gray-700">
                Finn og kontakt de beste talentene
              </p>
            </div>

            {/* Payment Failed Warning */}
            {subscriptionData?.status === 'past_due' && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-8 max-w-4xl mx-4 xl:mx-auto">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-900">Betalingsproblem</span>
                </div>
                <p className="text-sm text-red-800 mb-3">
                  Din siste betaling feilet. Oppdater betalingsmetoden din for å fortsette.
                  {typeof subscriptionData?.daysLeftInGrace === 'number' && (
                    <span className="block mt-1 font-medium">
                      Du har <strong>{subscriptionData.daysLeftInGrace} dag{subscriptionData.daysLeftInGrace !== 1 ? 'er' : ''}</strong> igjen for å oppdatere betalingsdetaljene før tilgangen opphører.
                    </span>
                  )}
                </p>
                <button
                  onClick={() => {
                    // For Nets there is no separate customer portal; we reuse the embedded checkout to update card details.
                    void handleOpenUpdatePayment();
                  }}
                  disabled={billingPortalLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {billingPortalLoading ? 'Åpner...' : 'Oppdater betalingsmetode'}
                </button>
              </div>
            )}

            {/* ✅ NEW: Pending Downgrade Banner */}
            {hasPendingDowngrade && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mb-8 max-w-4xl mx-auto">
                <div className="flex items-center gap-2 mb-2">
                  <ChevronLeft className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">Nedgradering planlagt</span>
                </div>
                <p className="text-sm text-blue-800 mb-3">
                  Ditt abonnement vil endre til <strong>{pendingPlan === 'employer' ? 'Basis' : 'Pluss'}</strong>
                  {subscriptionEndDateFormatted ? (
                    <> fra <strong>{subscriptionEndDateFormatted}</strong>.</>
                  ) : (
                    <> ved neste faktureringsperiode.</>
                  )}
                  {pendingPlan === 'employer' && ' Lagrede søk vil bli skjult, men kan gjenopprettes ved oppgradering. Favoritter beholdes i Basis.'}
                </p>
                <button
                  onClick={() => setShowCancelDowngradeModal(true)}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Angre nedgradering
                </button>
              </div>
            )}

            {/* Employer Verification Status Warning */}
            {userProfile?.userType === 'employer' && userProfile?.verificationStatus === 'pending' && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-8 max-w-4xl mx-auto">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-2">
                      Status: Venter på godkjenning
                    </h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      Vi behandler bedriftsprofilen din. Du får e-post når du kan kjøpe abonnement.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 3 Employer Plans */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-12 px-4 xl:px-0">
              {/* Employer Free */}
              <div className={`bg-white rounded-3xl shadow-lg p-6 border-2 ${currentPlan === 'free' ? 'border-purple-400' : 'border-gray-200'}`}>
                <h3 className="text-2xl mb-2">Gratis</h3>
                <div className="mb-6">
                  <span className="text-4xl">0 kr</span>
                  <span className="text-gray-500">/måned</span>
                </div>
                
                <ul className="space-y-3 mb-6 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Ubegrenset søk</strong> etter talenter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Se profiler og portfolioer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">Ingen meldinger</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">Ingen favoritter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">Ingen lagrede søk</span>
                  </li>
                </ul>

                {currentPlan === 'free' && (
                  <div className="text-center text-sm text-gray-600 py-2 bg-gray-50 rounded-lg">
                    Din nåværende plan
                  </div>
                )}
              </div>

              {/* Employer Messaging (490 kr) */}
              <div className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl shadow-xl p-6 text-white ${currentPlan === 'employer' ? 'ring-4 ring-blue-300' : ''}`}>
                <h3 className="text-2xl mb-2 flex items-center gap-2">
                  <MessageCircle className="w-6 h-6" />
                  Basis
                </h3>
                <div className="mb-6">
                  <span className="text-4xl">490 kr</span>
                  <span className="text-blue-100">/måned</span>
                </div>
                
                <ul className="space-y-3 mb-6 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span>Alt i gratis-planen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span><strong>Ubegrensede meldinger</strong> til alle talenter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span>Kontakt talenter direkte</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span><strong>Vis interesse for oppdrag</strong> fra oppdragsoversikten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span className="text-blue-100">Favoritter inkludert</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                    <span className="text-blue-100">Ingen lagrede søk</span>
                  </li>
                </ul>

                {/* ✅ FIX: Show status when profile says employer (source of truth); isActive from API may lag after purchase */}
                {currentPlan === 'employer' && (isActive || hasEmployerSubscription) ? (
                  <div>
                    {isCanceled ? (
                      <div className="bg-orange-100 rounded-2xl p-3 mb-3 text-center">
                        <p className="text-sm text-orange-900">
                          {daysRemaining !== null && daysRemaining > 0 ? (
                            <>Du har <strong>{daysRemaining} dag{daysRemaining !== 1 ? 'er' : ''}</strong> igjen av dine fordeler</>
                          ) : subscriptionEndDateFormatted ? (
                            <>Du har tilgang til {subscriptionEndDateFormatted}</>
                          ) : (
                            <strong>Utløper snart</strong>
                          )}
                        </p>
                        <p className="text-xs text-orange-700 mt-1">Abonnementet fornyes ikke automatisk</p>
                      </div>
                    ) : (
                      <div className="bg-white/20 rounded-2xl p-3 mb-3 text-center backdrop-blur-sm">
                        <p className="text-sm font-semibold">✓ Aktiv plan</p>
                        <p className="text-xs mt-1 opacity-90">
                          {subscriptionEndDateFormatted ? `Fornyes automatisk den ${subscriptionEndDateFormatted}` : 'Fornyes automatisk'}
                        </p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          devLog('🔴 Avslutt abonnement klikket (Arbeidsgiver Messaging)');
                          if (isCanceled) {
                            if (onOpenReactivateModal) {
                              onOpenReactivateModal(currentPlan, handleReactivate);
                            }
                          } else if (onOpenCancelModal) {
                            onOpenCancelModal(subscriptionEndDateISO, currentPlan, handleCancel);
                          }
                        }}
                        className="w-full px-4 py-3 bg-white text-blue-600 rounded-xl hover:shadow-xl transform hover:scale-105 transition-all text-sm"
                      >
                        {isCanceled ? 'Reaktiver' : 'Avslutt abonnement'}
                      </button>
                      <button
                        onClick={() => handleOpenUpdatePayment()}
                        disabled={billingPortalLoading}
                        className="w-full px-4 py-3 bg-white/20 text-white border border-white/40 rounded-xl hover:bg-white/30 transition-all text-sm flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        {billingPortalLoading ? 'Åpner...' : 'Endre betalingsmåte'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      // ✅ FIX: If user has employer_plus, show downgrade confirmation modal
                      if (currentPlan === 'employer_plus') {
                        setShowDowngradeModal(true);
                      } else {
                        handleCheckoutClick('arbeidsgiver', priceIds?.arbeidsgiverMessagingMonthly || 'arbeidsgiver_messaging_monthly');
                      }
                    }}
                    disabled={hasPendingDowngrade || loading || isCanceled || !canPurchaseEmployerSubscription(userProfile, currentPlan)}
                    className={`w-full px-4 py-3 rounded-xl transition-all text-sm flex items-center justify-center gap-2 ${
                      hasPendingDowngrade || loading || isCanceled || !canPurchaseEmployerSubscription(userProfile, currentPlan)
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                        : 'bg-white text-blue-600 hover:shadow-xl transform hover:scale-105'
                    }`}
                  >
                    {currentPlan === 'employer_plus' ? (
                      <>
                        <ChevronLeft className="w-4 h-4" />
                        {hasPendingDowngrade 
                          ? 'Nedgradering planlagt' 
                          : isCanceled 
                            ? 'Reaktiver for å nedgradere'
                            : loading 
                              ? 'Behandler...' 
                              : 'Nedgrader til meldingstilgang'
                        }
                      </>
                    ) : (
                      <>
                        Kjøp meldingstilgang
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Employer Plus (990 kr) */}
              <div className={`bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 rounded-3xl shadow-2xl p-6 text-white relative ${currentPlan === 'employer_plus' ? 'ring-4 ring-yellow-300' : ''}`}>
                <div className="absolute top-0 right-0 bg-yellow-400 text-purple-900 px-3 py-1 rounded-bl-2xl rounded-tr-3xl text-xs flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Anbefalt
                </div>
                
                <h3 className="text-2xl mb-2 flex items-center gap-2">
                  <Crown className="w-6 h-6 text-yellow-300" />
                  Pluss
                </h3>
                <div className="mb-6">
                  <span className="text-4xl">990 kr</span>
                  <span className="text-purple-200">/måned</span>
                </div>
                
                <ul className="space-y-3 mb-6 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span>Alt i meldingstilgang</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span><strong>Vis interesse for oppdrag</strong> fra oppdragsoversikten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Briefcase className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span><strong>Publiser oppdrag</strong> – legg ut oppdrag som er synlige for alle brukere</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Heart className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span><strong>Favoritter</strong> - lagre ubegrenset antall talenter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Bookmark className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span><strong>Lagrede søk</strong> - maks 3 søk med varsling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span>Støtt utviklingen av Talentfunn</span>
                  </li>
                </ul>

                {/* ✅ FIX: Show status when profile says employer_plus (source of truth); isActive from API may lag after purchase */}
                {currentPlan === 'employer_plus' && (isActive || hasEmployerPlusSubscription) ? (
                  <div>
                    {isCanceled ? (
                      <div className="bg-orange-100 rounded-2xl p-3 mb-3 text-center">
                        <p className="text-sm text-orange-900">
                          {daysRemaining !== null && daysRemaining > 0 ? (
                            <>Du har <strong>{daysRemaining} dag{daysRemaining !== 1 ? 'er' : ''}</strong> igjen av dine fordeler</>
                          ) : subscriptionEndDateFormatted ? (
                            <>Du har tilgang til {subscriptionEndDateFormatted}</>
                          ) : (
                            <strong>Utløper snart</strong>
                          )}
                        </p>
                        <p className="text-xs text-orange-700 mt-1">Abonnementet fornyes ikke automatisk</p>
                      </div>
                    ) : (
                      <div className="bg-white/20 rounded-2xl p-3 mb-3 text-center backdrop-blur-sm">
                        <p className="text-sm font-semibold">✓ Aktiv plan</p>
                        <p className="text-xs mt-1 opacity-90">
                          {subscriptionEndDateFormatted ? `Fornyes automatisk den ${subscriptionEndDateFormatted}` : 'Fornyes automatisk'}
                        </p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          devLog('🔴 Avslutt abonnement klikket (Arbeidsgiver Pluss)');
                          if (isCanceled) {
                            if (onOpenReactivateModal) {
                              onOpenReactivateModal(currentPlan, handleReactivate);
                            }
                          } else if (onOpenCancelModal) {
                            onOpenCancelModal(subscriptionEndDateISO, currentPlan, handleCancel);
                          }
                        }}
                        className="w-full px-4 py-3 bg-white text-purple-600 rounded-xl hover:shadow-xl transform hover:scale-105 transition-all text-sm"
                      >
                        {isCanceled ? 'Reaktiver' : 'Avslutt abonnement'}
                      </button>
                      <button
                        onClick={() => handleOpenUpdatePayment()}
                        disabled={billingPortalLoading}
                        className="w-full px-4 py-3 bg-white/20 text-white border border-white/40 rounded-xl hover:bg-white/30 transition-all text-sm flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        {billingPortalLoading ? 'Åpner...' : 'Endre betalingsmåte'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleCheckoutClick('arbeidsgiver_plus', priceIds?.arbeidsgiverPlussMonthly || 'arbeidsgiver_pluss_monthly')}
                    disabled={!canPurchaseEmployerSubscription(userProfile, currentPlan)}
                    className={`w-full px-4 py-3 rounded-xl transition-all text-sm flex items-center justify-center gap-2 ${
                      !canPurchaseEmployerSubscription(userProfile, currentPlan)
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                        : 'bg-white text-purple-600 hover:shadow-xl transform hover:scale-105'
                    }`}
                  >
                    {currentPlan === 'employer' ? (
                      <>
                        Oppgrader til Pluss
                        <ChevronRight className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Kjøp Pluss
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Employer FAQ - flat on mobile, inline on desktop */}
            <div className="p-8 max-w-3xl mx-auto mx-4 xl:bg-transparent xl:rounded-none xl:shadow-none xl:max-w-none xl:mx-0 xl:pt-10 xl:border-t xl:border-gray-200">
              <h2 className="text-2xl mb-6 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Ofte stilte spørsmål
              </h2>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-5 h-5 text-purple-600" />
                    <h3>Hva er forskjellen på Basis og Pluss?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    <strong>Basis (490 kr/mnd):</strong> Send meldinger til talenter, kontakt kandidater direkte, vis interesse for oppdrag og lagre favoritter.<br/>
                    <strong>Pluss (990 kr/mnd):</strong> Alt i Basis, pluss publisering av oppdrag, lagrede søk med varsling, og støtte utviklingen av Talentfunn.
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-purple-600" />
                    <h3>Er dette kun for bedrifter?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Ja. Arbeidsgiver-abonnementer er kun tilgjengelig som bedriftskjøp (B2B). Du må oppgi organisasjonsnummer ved kjøp for korrekt MVA-håndtering og fakturering.
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-5 h-5 text-purple-600" />
                    <h3>Hvordan fungerer favoritter?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Lagre talentprofiler du er interessert i for rask tilgang senere. Perfekt for å holde styr på kandidater du vurderer. Tilgjengelig med Basis- og Pluss-abonnement.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Bookmark className="w-5 h-5 text-purple-600" />
                    <h3>Hvordan fungerer lagrede søk?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Lagre søk du bruker ofte, og få varsling når nye talenter matcher dine søk. Spar tid på rekruttering! Kun tilgjengelig med Pluss-abonnement.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className="w-5 h-5 text-purple-600" />
                    <h3>Kan jeg bytte plan?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Ja! Du kan oppgradere fra Basis til Pluss når som helst. Endringen trer i kraft ved neste fornyelse.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-purple-600" />
                    <h3>Hva skjer hvis jeg kansellerer eller nedgraderer?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Du beholder tilgang til slutten av perioden. Ved nedgradering fra Pluss til Basis beholder du meldinger og favoritter, men mister lagrede søk. Ved nedgradering til gratis mister du meldinger, favoritter og lagrede søk. Alle dataene dine beholdes trygt.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                    <h3>Har jeg angrefrist?</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Nei, næringskjøp (B2B) har ikke lovfestet angrefrist. Abonnementet fornyes automatisk månedlig og kan kanselleres når som helst.
                  </p>
                </div>
              </div>
            </div>
            
            {/* ✅ Upgrade Confirmation Modal (Employer View) */}
            {showUpgradeModal && pendingUpgrade && (
              <UpgradeConfirmationModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                onConfirm={handleConfirmUpgrade}
                planName={pendingUpgrade.planName}
                planType={pendingUpgrade.planType}
                priceId={pendingUpgrade.priceId}
                monthlyPrice={pendingUpgrade.monthlyPrice}
              />
            )}
          </div>
            
            {/* ✅ Downgrade Confirmation Modal */}
            {showDowngradeModal && ReactDOM.createPortal(
              <div 
                className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center px-4 pt-safe pb-24 pb-safe"
                style={{ zIndex: 'var(--z-modal)' }}
                onClick={() => setShowDowngradeModal(false)}
              >
                <div 
                  className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-semibold mb-3">Nedgrader til Basis?</h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    Du vil beholde ubegrensede meldinger til talenter og favoritter, men mister lagrede søk.
                    Endringen skjer ved periodens slutt{subscriptionEndDateFormatted ? ` (${subscriptionEndDateFormatted})` : ''}.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDowngradeModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Avbryt
                    </button>
                    <button
                      onClick={async () => {
                        setDowngradeLoading(true);
                        try {
                          await handleDowngrade();
                          setShowDowngradeModal(false);
                        } finally {
                          setDowngradeLoading(false);
                        }
                      }}
                      disabled={downgradeLoading}
                      className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        downgradeLoading
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {downgradeLoading ? (
                        <>
                          <Loader size="sm" className="text-white" />
                          Nedgraderer...
                        </>
                      ) : (
                        'Nedgrader'
                      )}
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
            
            {/* ✅ Cancel Downgrade Confirmation Modal */}
            {showCancelDowngradeModal && ReactDOM.createPortal(
              <div 
                className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center px-4 pt-safe pb-24 pb-safe"
                style={{ zIndex: 'var(--z-modal)' }}
                onClick={() => setShowCancelDowngradeModal(false)}
              >
                <div 
                  className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-semibold mb-3">Angre nedgradering?</h3>
                  <p className="text-gray-600 mb-6 text-sm">
                    Du vil beholde din nåværende plan (Arbeidsgiver Pluss - 990 kr/mnd). Den planlagte nedgraderingen blir kansellert.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowCancelDowngradeModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Avbryt
                    </button>
                    <button
                      onClick={async () => {
                        setCancelDowngradeLoading(true);
                        try {
                          const response = await fetch(
                            `${API_BASE}/nets/cancel-downgrade`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${accessToken}`,
                              },
                            }
                          );

                          const data = await response.json();

                          if (response.ok && data.success) {
                            toast.success('Nedgradering angret! Du beholder din nåværende plan.');
                            fetchSubscriptionStatus();
                            setShowCancelDowngradeModal(false);
                          } else {
                            toast.error('Feil: ' + (data.error || 'Kunne ikke angre nedgradering'));
                          }
                        } catch (error) {
                          devError('Cancel downgrade error:', error);
                          toast.error('Kunne ikke angre nedgradering');
                        } finally {
                          setCancelDowngradeLoading(false);
                        }
                      }}
                      disabled={cancelDowngradeLoading}
                      className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        cancelDowngradeLoading
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {cancelDowngradeLoading ? (
                        <>
                          <Loader size="sm" className="text-white" />
                          Angrer...
                        </>
                      ) : (
                        'Bekreft'
                      )}
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          </PageShell>
        </div>
      </div>
    );
  }

  // TALENT VIEW - 2 Plans: Free and Plus
  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-orange-50 xl:overflow-hidden">
      <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />
      {subPageHeader}
      <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-6xl xl:mx-auto xl:py-6">
          {/* Content container - rounded on desktop only */}
          <div className="xl:pt-5 xl:bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:p-8">
            {/* Header */}
            <div className="text-center mb-12 px-4 xl:px-0">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Crown className="w-12 h-12 text-yellow-500" />
              <h1 className="text-5xl bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-1">
                Talentfunn Pluss
              </h1>
              <Crown className="w-12 h-12 text-yellow-500" />
            </div>
            <p className="text-xl text-gray-700">
              Få mer synlighet og ubegrensede muligheter
            </p>
          </div>

            {/* Payment Failed Warning */}
            {subscriptionData?.status === 'past_due' && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-8 max-w-2xl mx-4 xl:mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-900">Betalingsproblem</span>
              </div>
              <p className="text-sm text-red-800 mb-3">
                Din siste betaling feilet. Oppdater betalingsmetoden din for å fortsette med Pluss-fordelene.
                {typeof subscriptionData?.daysLeftInGrace === 'number' && (
                  <span className="block mt-1 font-medium">
                    Du har <strong>{subscriptionData.daysLeftInGrace} dag{subscriptionData.daysLeftInGrace !== 1 ? 'er' : ''}</strong> igjen for å oppdatere betalingsdetaljene før tilgangen opphører.
                  </span>
                )}
              </p>
              <button
                onClick={() => {
                  void handleOpenUpdatePayment();
                }}
                disabled={billingPortalLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {billingPortalLoading ? 'Åpner...' : 'Oppdater betalingsmetode'}
              </button>
            </div>
          )}

            {/* 2 Talent Plans */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-12 px-4 xl:px-0">
            {/* Talent Free */}
            <div className={`bg-white rounded-3xl shadow-lg p-8 border-2 ${currentPlan === 'free' ? 'border-purple-400' : 'border-gray-200'}`}>
              <h3 className="text-3xl mb-2">Gratis</h3>
              <div className="mb-6">
                <span className="text-5xl">0 kr</span>
                <span className="text-gray-500">/måned</span>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Opprett profil og vis frem ferdigheter</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Motta meldinger fra arbeidsgivere</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>1 portfolioprosjekt</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>3 bilder</strong> per prosjekt</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">Ingen videoopplasting</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">Standard synlighet i søk</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">Ingen Pluss-badge</span>
                </li>
              </ul>

              {currentPlan === 'free' && (
                <div className="text-center text-gray-600 py-3 bg-gray-50 rounded-xl">
                  Din nåværende plan
                </div>
              )}
            </div>

            {/* Talent Plus */}
            <div className={`bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 rounded-3xl shadow-2xl p-8 text-white relative overflow-hidden ${currentPlan === 'plus' ? 'ring-4 ring-yellow-300' : ''}`}>
              <div className="absolute top-0 right-0 bg-yellow-400 text-purple-900 px-4 py-1 rounded-bl-2xl text-sm flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                Populært
              </div>
              
              <h3 className="text-3xl mb-2 flex items-center gap-2">
                <Crown className="w-7 h-7 text-yellow-300" />
                Pluss
              </h3>
              <div className="mb-6">
                <span className="text-5xl">149 kr</span>
                <span className="text-purple-200">/måned</span>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span>Alt i gratis-planen</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span><strong>6 portfolioprosjekter</strong> (opp fra 1)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Video className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span><strong>Videoopplasting</strong> - 1 video per prosjekt</span>
                </li>
                <li className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span><strong>Vis interesse for oppdrag</strong> direkte fra oppdragsoversikten</span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span><strong>3x høyere</strong> synlighet i søkeresultater</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span><strong>Prioritert</strong> plassering på forsiden</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span><strong>Eksklusiv Pluss-badge</strong> på profilen</span>
                </li>
              </ul>

              {currentPlan === 'plus' && showAsActive ? (
                <div>
                  {isCanceled ? (
                    <div className="bg-orange-100 rounded-2xl p-4 mb-4 text-center shadow-lg border-2 border-orange-300">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <RefreshCw className="w-5 h-5 text-orange-600" />
                        <span className="text-orange-900 font-semibold">Abonnement avsluttet</span>
                      </div>
                      <p className="text-sm text-orange-800 mb-1">
                        {daysRemaining !== null && daysRemaining > 0 ? (
                          <>Du har <strong>{daysRemaining} dag{daysRemaining !== 1 ? 'er' : ''}</strong> igjen av dine fordeler</>
                        ) : (
                          <>Du har tilgang til {subscriptionEndDateFormatted}</>
                        )}
                      </p>
                      <p className="text-xs text-orange-700">
                        Abonnementet fornyes ikke automatisk
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl p-4 mb-4 text-center shadow-lg">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Crown className="w-5 h-5 text-yellow-500" />
                        <span className="text-purple-900 font-semibold">Du er Pluss-medlem!</span>
                      </div>
                      <p className="text-sm text-purple-700 flex items-center justify-center gap-1 mb-1">
                        Takk for din støtte
                        <Heart className="w-4 h-4 fill-current text-pink-500" />
                      </p>
                      <p className="text-xs text-purple-600">
                        Fornyes automatisk den {subscriptionEndDateFormatted}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        devLog('🔴 Avslutt abonnement klikket (Talent Plus)');
                        if (isCanceled) {
                          if (onOpenReactivateModal) {
                            onOpenReactivateModal(currentPlan, handleReactivate);
                          }
                        } else if (onOpenCancelModal) {
                          onOpenCancelModal(subscriptionEndDateISO, currentPlan, handleCancel);
                        }
                      }}
                      className="w-full px-8 py-4 bg-white text-purple-900 rounded-2xl hover:bg-purple-50 transition-all shadow-md"
                    >
                      {isCanceled ? 'Reaktiver abonnement' : 'Avslutt abonnement'}
                    </button>
                    <button
                      onClick={() => handleOpenUpdatePayment()}
                      disabled={billingPortalLoading}
                      className="w-full px-8 py-3 bg-purple-100 text-purple-800 rounded-2xl hover:bg-purple-200 transition-all shadow-md flex items-center justify-center gap-2"
                    >
                      <CreditCard className="w-4 h-4" />
                      {billingPortalLoading ? 'Åpner...' : 'Endre betalingsmåte'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => handleCheckoutClick('talent_plus', priceIds?.talentPlussMonthly || 'talent_pluss_monthly')}
                  className="w-full px-8 py-4 bg-white text-purple-600 rounded-2xl hover:shadow-2xl transition-all transform hover:scale-105 text-lg flex items-center justify-center gap-2"
                >
                  Oppgrader til Pluss
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

            {/* Talent FAQ - flat on mobile, inline on desktop */}
            <div className="p-8 max-w-3xl mx-auto mx-4 xl:bg-transparent xl:rounded-none xl:shadow-none xl:max-w-none xl:mx-0 xl:pt-10 xl:border-t xl:border-gray-200">
            <h2 className="text-2xl mb-6 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
              Ofte stilte spørsmål
            </h2>
            
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-purple-600" />
                  <h3>Hva får jeg med Pluss?</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Med Talentfunn Pluss (149 kr/mnd) får du opptil 6 porteføljeprosjekter (opp fra 1), mulighet til å laste opp video i prosjektene dine, vise interesse for oppdrag, starte nye samtaler med andre brukere, publisere egne tjenester på profilen din, 3x høyere synlighet i søkeresultater, prioritert plassering på forsiden, og eksklusiv Pluss-badge. Dette øker synligheten din betydelig hos arbeidsgivere og andre talenter.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Video className="w-5 h-5 text-purple-600" />
                  <h3>Kan jeg ha video i prosjektene mine?</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Ja! Med Pluss-abonnement kan du laste opp både bilder og video i samme prosjekt. Video lagres via Bunny.net (EU), og kan være offentlig synlig når du har samtykket til dette ved opplasting.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  <h3>Hvordan får jeg bedre synlighet?</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Pluss-brukere vises høyere i søkeresultater og får prioritert plassering på forsiden. Dette øker sjansen for at arbeidsgivere finner deg, og du får en eksklusiv Pluss-badge på profilen.
                </p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                  <h3>Hva skjer hvis jeg kansellerer?</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Du beholder Pluss-fordeler til slutten av perioden og mottar et varsel når perioden snart er over. Du beholder alle publiserte prosjekter, men velger maks 1 som vises offentlig. Har du lagt inn video i prosjektet vil denne fortsatt vises, men du vil ikke kunne endre eller legge til en ny. Nye samtaler krever Pluss, men du kan fortsatt svare i eksisterende samtaler. Tjenesteforespørsler kan fortsatt sendes av alle innloggede brukere. Aktive tjenester avpubliseres automatisk ved nedgradering, og du kan ikke publisere dem igjen før du har Talentfunn Pluss på nytt. Du kan reaktivere abonnementet når som helst.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <h3>Hvordan betaler jeg?</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Vi bruker Nets/Nexi for sikker betaling. Abonnementet fornyes månedlig og kan kanselleres når som helst.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  <h3>Har jeg angrefrist?</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Talentfunn Pluss leveres umiddelbart. I henhold til Angrerettloven § 22 bokstav n bortfaller angreretten når du samtykker til umiddelbar levering. Du kan likevel si opp abonnementet når som helst. Se "Vilkår og betingelser" for full informasjon.
                </p>
              </div>
            </div>
          </div>
            
            {/* Upgrade Confirmation Modal */}
            {showUpgradeModal && pendingUpgrade && (
              <UpgradeConfirmationModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                onConfirm={handleConfirmUpgrade}
                planName={pendingUpgrade.planName}
                planType={pendingUpgrade.planType}
                priceId={pendingUpgrade.priceId}
                monthlyPrice={pendingUpgrade.monthlyPrice}
              />
            )}
          </div>
        </PageShell>
      </div>
    </div>
    );
  }
